import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { vendorSettingsApi } from '../../services/api';
import { toast } from 'sonner';
import { Settings, Save, Upload, Loader2 } from 'lucide-react';

const VendorSettingsPage = () => {
  const fileRef = useRef(null);
  const [data, setData] = useState({
    company_name: 'EduTrack',
    address: '',
    phone: '',
    email: '',
    website: '',
    gstin: '',
    pan: '',
    logo_url: '',
    bank_name: '',
    bank_account: '',
    bank_ifsc: '',
    upi_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await vendorSettingsApi.get();
      setData((d) => ({ ...d, ...r.data }));
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await vendorSettingsApi.update(data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await vendorSettingsApi.uploadLogo(fd);
      setData((d) => ({ ...d, logo_url: r.data.logo_url }));
      toast.success('Logo uploaded');
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const field = (key, label, placeholder) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input
        value={data[key] || ''}
        onChange={(e) => setData({ ...data, [key]: e.target.value })}
        placeholder={placeholder}
        data-testid={`vendor-${key}-input`}
      />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vendor-settings-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
          <Settings className="h-7 w-7 text-primary" /> Vendor Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Your business identity shown on every quotation and invoice.
        </p>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <>
          <Card className="border border-border">
            <CardHeader><CardTitle>Identity &amp; contact</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-20 w-20 rounded border flex items-center justify-center bg-muted/30 overflow-hidden">
                  {data.logo_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${data.logo_url}`}
                      alt="vendor logo"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">No logo</span>
                  )}
                </div>
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => onLogo(e.target.files?.[0])}
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-vendor-logo"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                    Upload logo (PNG / JPG)
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {field('company_name', 'Company / Brand name', 'EduTrack')}
                {field('email', 'Contact email', 'contact@edutrack.in')}
                {field('phone', 'Contact phone', '+91-XXXXXXXXXX')}
                {field('website', 'Website', 'edutrack.in')}
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={data.address || ''}
                  onChange={(e) => setData({ ...data, address: e.target.value })}
                  placeholder="Office address shown on the quotation header"
                  rows={2}
                  data-testid="vendor-address-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardHeader>
              <CardTitle>Tax details</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Leave GSTIN blank for now — when you get it, fill it here and it'll auto-appear on every quote / invoice.
              </p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('gstin', 'GSTIN (when available)', 'e.g., 29ABCDE1234F1Z5')}
              {field('pan', 'PAN', 'e.g., ABCDE1234F')}
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardHeader><CardTitle>Bank &amp; payment</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {field('bank_name', 'Bank name', '')}
              {field('bank_account', 'Account number', '')}
              {field('bank_ifsc', 'IFSC', '')}
              {field('upi_id', 'UPI ID', 'name@bank')}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-vendor-settings">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save settings
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorSettingsPage;
